import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import ProfilePhotoInput from "@/components/site/ProfilePhotoInput";
import api from "@/lib/api";
import { CONTACT } from "@/lib/contact";
import { useT } from "@/lib/i18n";
import LanguageSwitch from "@/components/site/LanguageSwitch";

const PROGRAMS = [
  { key: "playgroup", labelKey: "programs.playgroup" },
  { key: "nursery", labelKey: "programs.nursery" },
  { key: "lkg", labelKey: "programs.lkg" },
  { key: "ukg", labelKey: "programs.ukg" },
  { key: "daycare", labelKey: "programs.daycare" },
];

const PICKUP_OPTIONS = [
  { key: "parent", labelKey: "af.pickup.parent" },
  { key: "grandparent", labelKey: "af.pickup.grandparent" },
  { key: "driver", labelKey: "af.pickup.driver" },
  { key: "van", labelKey: "af.pickup.van" },
  { key: "other", labelKey: "af.pickup.other" },
];

const DOCUMENT_OPTIONS = [
  { key: "birth_certificate", labelKey: "af.doc.birth" },
  { key: "aadhaar_copy", labelKey: "af.doc.aadhaar" },
  { key: "passport_photos", labelKey: "af.doc.photos" },
  { key: "vaccination_record", labelKey: "af.doc.vaccine" },
  { key: "address_proof", labelKey: "af.doc.address" },
  { key: "parent_id_proof", labelKey: "af.doc.parentId" },
  { key: "transfer_certificate", labelKey: "af.doc.tc" },
];

const empty = {
  admission_no: "", academic_year: "", date_of_admission: "",
  admission_seeking: "",
  student_first_name: "", student_middle_name: "", student_last_name: "",
  date_of_birth: "", gender: "", blood_group: "", aadhaar: "",
  nationality: "Indian", mother_tongue: "", other_languages: "", religion: "", category: "",
  student_photo: null,
  house_no: "", street: "", area: "", landmark: "",
  village_city: "", taluk: "", district: "", state: "Karnataka", pin_code: "",
  father_name: "", father_qualification: "", father_occupation: "", father_company: "",
  father_mobile: "", father_whatsapp: "", father_email: "",
  mother_name: "", mother_qualification: "", mother_occupation: "", mother_company: "",
  mother_mobile: "", mother_whatsapp: "", mother_email: "",
  special_needs: "", allergies: "", doctor_name: "", medical_conditions: "",
  hospital: "", current_medication: "", emergency_contact_number: "",
  prev_school: "", prev_class: "", prev_reason: "",
  pickup_types: [], pickup_other: "",
  authorized_person_name: "", authorized_relationship: "", authorized_phone: "", authorized_address: "",
  emergency_name: "", emergency_relationship: "", emergency_phone: "", emergency_alt_phone: "",
  documents_submitted: [],
  declaration_agreed: false,
};

const STEPS = [
  { key: "student", labelKey: "af.step.student" },
  { key: "address", labelKey: "af.step.address" },
  { key: "parents", labelKey: "af.step.parents" },
  { key: "medical", labelKey: "af.step.medical" },
  { key: "pickup", labelKey: "af.step.pickup" },
];

const Field = ({ id, label, children, required, cols = 1 }) => (
  <div className={cols === 2 ? "sm:col-span-2" : ""}>
    <Label htmlFor={id} className="text-xs font-semibold text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const TextIn = ({ id, value, onChange, ...rest }) => (
  <Input
    id={id}
    data-testid={`af-${id}`}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="rounded-2xl h-10 border-slate-200"
    {...rest}
  />
);

const AdmissionForm = () => {
  const { t } = useT();
  const { token } = useParams();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [reason, setReason] = useState("");
  const [step, setStep] = useState(0);
  const [f, setF] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggleArr = (k, key) => setF((s) => ({
    ...s,
    [k]: s[k].includes(key) ? s[k].filter((x) => x !== key) : [...s[k], key],
  }));

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await api.get(`/admission-links/${token}`);
        if (!alive) return;
        setValid(true);
      } catch (err) {
        if (!alive) return;
        setValid(false);
        setReason(err?.response?.data?.detail || "Invalid link");
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // ---- validation per step ----
  const stepValid = useMemo(() => {
    switch (STEPS[step].key) {
      case "student":
        return f.admission_seeking && f.student_first_name && f.date_of_birth && (f.gender === "male" || f.gender === "female");
      case "address":
        return true;
      case "parents":
        return f.father_name && f.father_mobile && f.mother_name && f.mother_mobile;
      case "medical":
        return true;
      case "pickup":
        return f.declaration_agreed;
      default:
        return true;
    }
  }, [step, f]);

  const submit = async () => {
    if (!f.declaration_agreed) { toast.error(t("af.errDeclaration")); return; }
    setSubmitting(true);
    try {
      await api.post(`/admission-links/${token}/submit`, f);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("af.errGeneric"));
    } finally { setSubmitting(false); }
  };

  // ---- render ----
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-ark-skyDeep" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center px-4">
        <Card className="border-0 rounded-3xl shadow-ark-soft max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
            <h1 className="font-display font-bold text-2xl text-ark-ink">{t("af.invalid.title")}</h1>
            <p className="text-slate-600" data-testid="af-invalid-reason">{reason}</p>
            <p className="text-sm text-slate-500">{t("af.invalid.help")}</p>
            <Link to="/" className="inline-block mt-4 rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold px-6 py-2.5">{t("af.invalid.home")}</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 flex items-center justify-center px-4">
        <Card className="border-0 rounded-3xl shadow-ark-soft max-w-md w-full">
          <CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
            <h1 className="font-display font-bold text-2xl text-ark-ink">{t("af.done.title")}</h1>
            <p className="text-slate-600" data-testid="af-success">
              {t("af.done.body", { name: f.student_first_name })}
            </p>
            <Link to="/" className="inline-block rounded-full bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold px-6 py-2.5">{t("af.done.home")}</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50 py-8 px-4">
      <div className="mx-auto max-w-3xl">
        {/* Header */}
        <div className="relative text-center mb-8">
          <div className="absolute right-0 top-0"><LanguageSwitch compact /></div>
          <img src={CONTACT.logoUrl} alt="Little Ark" className="h-16 w-auto object-contain mx-auto" />
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-ark-skyDeep mt-4">{t("af.title")}</h1>
          <p className="text-slate-600 mt-1">{t("af.subtitle")}</p>
          <p className="text-xs text-slate-500 mt-1">{t("af.subtitle2")}</p>
        </div>

        {/* Stepper */}
        <div className="mb-6 flex items-center gap-2 justify-center flex-wrap" data-testid="af-stepper">
          {STEPS.map((s, i) => (
            <button key={s.key} type="button" onClick={() => i < step && setStep(i)}
              disabled={i > step}
              className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
                i === step
                  ? "bg-ark-sky text-white border-ark-sky"
                  : i < step
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    : "bg-white text-slate-400 border-slate-200"
              }`}>
              {i + 1}. {t(s.labelKey)}
            </button>
          ))}
        </div>

        <Card className="border-0 rounded-3xl shadow-ark-soft">
          <CardContent className="p-6 sm:p-8">
            <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              {STEPS[step].key === "student" && (
                <div className="space-y-6" data-testid="af-step-student">
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.seeking")} <span className="text-red-500">*</span></h2>
                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="af-programs">
                      {PROGRAMS.map((p) => (
                        <button key={p.key} type="button" data-testid={`af-prog-${p.key}`}
                          onClick={() => set("admission_seeking")(p.key)}
                          aria-pressed={f.admission_seeking === p.key}
                          className={`text-sm font-semibold rounded-2xl px-3 py-3 border transition ${
                            f.admission_seeking === p.key
                              ? "bg-ark-sky text-white border-ark-sky"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}>
                          {t(p.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink mt-6">{t("af.section.student")}</h2>
                    <div className="grid sm:grid-cols-3 gap-4 mt-4">
                      <Field id="student_first_name" label={t("af.field.firstName")} required>
                        <TextIn id="student_first_name" value={f.student_first_name} onChange={set("student_first_name")} />
                      </Field>
                      <Field id="student_middle_name" label={t("af.field.middleName")}>
                        <TextIn id="student_middle_name" value={f.student_middle_name} onChange={set("student_middle_name")} />
                      </Field>
                      <Field id="student_last_name" label={t("af.field.lastName")}>
                        <TextIn id="student_last_name" value={f.student_last_name} onChange={set("student_last_name")} />
                      </Field>
                      <Field id="date_of_birth" label={t("af.field.dob")} required>
                        <TextIn id="date_of_birth" type="date" value={f.date_of_birth} onChange={set("date_of_birth")} />
                      </Field>
                      <Field id="gender" label={t("af.field.gender")} required>
                        <RadioGroup value={f.gender} onValueChange={set("gender")} className="flex gap-4 mt-2" data-testid="af-gender">
                          <label className="flex items-center gap-2 text-sm text-slate-700"><RadioGroupItem value="male" id="g-m" data-testid="af-gender-male" /> {t("af.field.male")}</label>
                          <label className="flex items-center gap-2 text-sm text-slate-700"><RadioGroupItem value="female" id="g-f" data-testid="af-gender-female" /> {t("af.field.female")}</label>
                        </RadioGroup>
                      </Field>
                      <Field id="blood_group" label={t("af.field.bloodGroup")}>
                        <TextIn id="blood_group" value={f.blood_group} onChange={set("blood_group")} placeholder={t("af.field.bloodGroupPh")} />
                      </Field>
                      <Field id="aadhaar" label={t("af.field.aadhaar")} cols={2}>
                        <TextIn id="aadhaar" value={f.aadhaar} onChange={set("aadhaar")} />
                      </Field>
                      <Field id="nationality" label={t("af.field.nationality")}>
                        <TextIn id="nationality" value={f.nationality} onChange={set("nationality")} />
                      </Field>
                      <Field id="mother_tongue" label={t("af.field.motherTongue")}>
                        <TextIn id="mother_tongue" value={f.mother_tongue} onChange={set("mother_tongue")} />
                      </Field>
                      <Field id="religion" label={t("af.field.religion")}>
                        <TextIn id="religion" value={f.religion} onChange={set("religion")} />
                      </Field>
                      <Field id="other_languages" label={t("af.field.otherLangs")}>
                        <TextIn id="other_languages" value={f.other_languages} onChange={set("other_languages")} />
                      </Field>
                      <Field id="category" label={t("af.field.category")}>
                        <TextIn id="category" value={f.category} onChange={set("category")} />
                      </Field>
                    </div>
                    <div className="mt-4">
                      <Label className="text-xs font-semibold text-slate-700">{t("af.field.photo")}</Label>
                      <div className="mt-2">
                        <ProfilePhotoInput value={f.student_photo} onChange={set("student_photo")} fallback="+" testId="af-photo" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {STEPS[step].key === "address" && (
                <div className="space-y-4" data-testid="af-step-address">
                  <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.address")}</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field id="house_no" label={t("af.field.house")}><TextIn id="house_no" value={f.house_no} onChange={set("house_no")} /></Field>
                    <Field id="street" label={t("af.field.street")}><TextIn id="street" value={f.street} onChange={set("street")} /></Field>
                    <Field id="area" label={t("af.field.area")}><TextIn id="area" value={f.area} onChange={set("area")} /></Field>
                    <Field id="landmark" label={t("af.field.landmark")}><TextIn id="landmark" value={f.landmark} onChange={set("landmark")} /></Field>
                    <Field id="village_city" label={t("af.field.city")}><TextIn id="village_city" value={f.village_city} onChange={set("village_city")} /></Field>
                    <Field id="taluk" label={t("af.field.taluk")}><TextIn id="taluk" value={f.taluk} onChange={set("taluk")} /></Field>
                    <Field id="district" label={t("af.field.district")}><TextIn id="district" value={f.district} onChange={set("district")} /></Field>
                    <Field id="state" label={t("af.field.state")}><TextIn id="state" value={f.state} onChange={set("state")} /></Field>
                    <Field id="pin_code" label={t("af.field.pin")}><TextIn id="pin_code" value={f.pin_code} onChange={set("pin_code")} inputMode="numeric" maxLength={6} /></Field>
                  </div>
                </div>
              )}

              {STEPS[step].key === "parents" && (
                <div className="space-y-6" data-testid="af-step-parents">
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.father")}</h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-3">
                      <Field id="father_name" label={t("af.field.fullName")} required><TextIn id="father_name" value={f.father_name} onChange={set("father_name")} /></Field>
                      <Field id="father_qualification" label={t("af.field.qualification")}><TextIn id="father_qualification" value={f.father_qualification} onChange={set("father_qualification")} /></Field>
                      <Field id="father_occupation" label={t("af.field.occupation")}><TextIn id="father_occupation" value={f.father_occupation} onChange={set("father_occupation")} /></Field>
                      <Field id="father_company" label={t("af.field.company")}><TextIn id="father_company" value={f.father_company} onChange={set("father_company")} /></Field>
                      <Field id="father_mobile" label={t("af.field.mobile")} required><TextIn id="father_mobile" value={f.father_mobile} onChange={set("father_mobile")} inputMode="tel" /></Field>
                      <Field id="father_whatsapp" label={t("af.field.whatsapp")}><TextIn id="father_whatsapp" value={f.father_whatsapp} onChange={set("father_whatsapp")} inputMode="tel" /></Field>
                      <Field id="father_email" label={t("af.field.emailId")} cols={2}><TextIn id="father_email" type="email" value={f.father_email} onChange={set("father_email")} /></Field>
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.mother")}</h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-3">
                      <Field id="mother_name" label={t("af.field.fullName")} required><TextIn id="mother_name" value={f.mother_name} onChange={set("mother_name")} /></Field>
                      <Field id="mother_qualification" label={t("af.field.qualification")}><TextIn id="mother_qualification" value={f.mother_qualification} onChange={set("mother_qualification")} /></Field>
                      <Field id="mother_occupation" label={t("af.field.occupation")}><TextIn id="mother_occupation" value={f.mother_occupation} onChange={set("mother_occupation")} /></Field>
                      <Field id="mother_company" label={t("af.field.company")}><TextIn id="mother_company" value={f.mother_company} onChange={set("mother_company")} /></Field>
                      <Field id="mother_mobile" label={t("af.field.mobile")} required><TextIn id="mother_mobile" value={f.mother_mobile} onChange={set("mother_mobile")} inputMode="tel" /></Field>
                      <Field id="mother_whatsapp" label={t("af.field.whatsapp")}><TextIn id="mother_whatsapp" value={f.mother_whatsapp} onChange={set("mother_whatsapp")} inputMode="tel" /></Field>
                      <Field id="mother_email" label={t("af.field.emailId")} cols={2}><TextIn id="mother_email" type="email" value={f.mother_email} onChange={set("mother_email")} /></Field>
                    </div>
                  </div>
                </div>
              )}

              {STEPS[step].key === "medical" && (
                <div className="space-y-6" data-testid="af-step-medical">
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.medical")}</h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-3">
                      <Field id="special_needs" label={t("af.field.specialNeeds")}><TextIn id="special_needs" value={f.special_needs} onChange={set("special_needs")} /></Field>
                      <Field id="allergies" label={t("af.field.allergies")}><TextIn id="allergies" value={f.allergies} onChange={set("allergies")} /></Field>
                      <Field id="medical_conditions" label={t("af.field.medConditions")}><TextIn id="medical_conditions" value={f.medical_conditions} onChange={set("medical_conditions")} /></Field>
                      <Field id="current_medication" label={t("af.field.medication")}><TextIn id="current_medication" value={f.current_medication} onChange={set("current_medication")} /></Field>
                      <Field id="doctor_name" label={t("af.field.doctor")}><TextIn id="doctor_name" value={f.doctor_name} onChange={set("doctor_name")} /></Field>
                      <Field id="hospital" label={t("af.field.hospital")}><TextIn id="hospital" value={f.hospital} onChange={set("hospital")} /></Field>
                      <Field id="emergency_contact_number" label={t("af.field.emergencyNumber")} cols={2}><TextIn id="emergency_contact_number" value={f.emergency_contact_number} onChange={set("emergency_contact_number")} inputMode="tel" /></Field>
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.prevSchool")}</h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-3">
                      <Field id="prev_school" label={t("af.field.prevSchool")}><TextIn id="prev_school" value={f.prev_school} onChange={set("prev_school")} /></Field>
                      <Field id="prev_class" label={t("af.field.prevClass")}><TextIn id="prev_class" value={f.prev_class} onChange={set("prev_class")} /></Field>
                      <Field id="prev_reason" label={t("af.field.prevReason")} cols={2}>
                        <Textarea id="prev_reason" value={f.prev_reason || ""} data-testid="af-prev_reason"
                          onChange={(e) => set("prev_reason")(e.target.value)}
                          className="rounded-2xl border-slate-200" rows={2} />
                      </Field>
                    </div>
                  </div>
                </div>
              )}

              {STEPS[step].key === "pickup" && (
                <div className="space-y-6" data-testid="af-step-pickup">
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.pickup")}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
                      {PICKUP_OPTIONS.map((p) => (
                        <button key={p.key} type="button" data-testid={`af-pickup-${p.key}`}
                          onClick={() => toggleArr("pickup_types", p.key)}
                          aria-pressed={f.pickup_types.includes(p.key)}
                          className={`text-xs font-semibold rounded-full px-3 py-2 border transition ${
                            f.pickup_types.includes(p.key)
                              ? "bg-ark-sky text-white border-ark-sky"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}>{t(p.labelKey)}</button>
                      ))}
                    </div>
                    {f.pickup_types.includes("other") && (
                      <div className="mt-3">
                        <TextIn id="pickup_other" value={f.pickup_other} onChange={set("pickup_other")} placeholder={t("af.field.pickupOther")} />
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-4 mt-4">
                      <Field id="authorized_person_name" label={t("af.field.authorizedName")}><TextIn id="authorized_person_name" value={f.authorized_person_name} onChange={set("authorized_person_name")} /></Field>
                      <Field id="authorized_relationship" label={t("af.field.relationship")}><TextIn id="authorized_relationship" value={f.authorized_relationship} onChange={set("authorized_relationship")} /></Field>
                      <Field id="authorized_phone" label={t("af.field.phoneNumber")}><TextIn id="authorized_phone" value={f.authorized_phone} onChange={set("authorized_phone")} inputMode="tel" /></Field>
                      <Field id="authorized_address" label={t("af.field.addressIfDiff")}><TextIn id="authorized_address" value={f.authorized_address} onChange={set("authorized_address")} /></Field>
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.emergency")}</h2>
                    <div className="grid sm:grid-cols-2 gap-4 mt-3">
                      <Field id="emergency_name" label={t("af.field.name")}><TextIn id="emergency_name" value={f.emergency_name} onChange={set("emergency_name")} /></Field>
                      <Field id="emergency_relationship" label={t("af.field.relationship")}><TextIn id="emergency_relationship" value={f.emergency_relationship} onChange={set("emergency_relationship")} /></Field>
                      <Field id="emergency_phone" label={t("af.field.phoneNumber")}><TextIn id="emergency_phone" value={f.emergency_phone} onChange={set("emergency_phone")} inputMode="tel" /></Field>
                      <Field id="emergency_alt_phone" label={t("af.field.altPhone")}><TextIn id="emergency_alt_phone" value={f.emergency_alt_phone} onChange={set("emergency_alt_phone")} inputMode="tel" /></Field>
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.docs")}</h2>
                    <div className="grid sm:grid-cols-2 gap-2 mt-3">
                      {DOCUMENT_OPTIONS.map((d) => (
                        <label key={d.key} className="flex items-center gap-2 rounded-2xl border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                          <Checkbox
                            checked={f.documents_submitted.includes(d.key)}
                            onCheckedChange={() => toggleArr("documents_submitted", d.key)}
                            data-testid={`af-doc-${d.key}`}
                          />
                          {t(d.labelKey)}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-lg text-ark-ink">{t("af.section.declaration")}</h2>
                    <label className="mt-3 flex items-start gap-3 rounded-2xl bg-slate-50 p-4 cursor-pointer">
                      <Checkbox
                        checked={f.declaration_agreed}
                        onCheckedChange={(v) => set("declaration_agreed")(!!v)}
                        data-testid="af-declaration"
                        className="mt-1"
                      />
                      <span className="text-sm text-slate-700">
                        {t("af.declarationText")}
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Nav buttons */}
            <div className="mt-8 flex items-center justify-between gap-3">
              <Button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0 || submitting}
                variant="outline"
                data-testid="af-prev"
                className="rounded-full h-11 px-5">
                <ArrowLeft className="h-4 w-4 mr-2" /> {t("af.back")}
              </Button>

              {step < STEPS.length - 1 ? (
                <Button
                  onClick={() => stepValid ? setStep((s) => s + 1) : toast.error(t("af.errRequired"))}
                  disabled={!stepValid}
                  data-testid="af-next"
                  className="rounded-full h-11 px-6 bg-ark-sky hover:bg-ark-skyDeep text-white font-display font-semibold">
                  {t("af.continue")} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={submitting || !stepValid}
                  data-testid="af-submit"
                  className="rounded-full h-11 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-semibold">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t("af.submitting")}</> : <>{t("af.submit")} <ShieldCheck className="h-4 w-4 ml-2" /></>}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-6">
          {CONTACT.schoolName} · {CONTACT.address}
        </p>
      </div>
    </div>
  );
};

export default AdmissionForm;
